import React from 'react';
import { useTranslation } from 'react-i18next';
import { useFormatters } from '../../../utils/format';
import Icon from '../../../components/AppIcon';

const PayBandVisualization = ({ payBandData }) => {
  const { t } = useTranslation();
  const { eur } = useFormatters();

  const calculatePosition = () => {
    const range = payBandData?.maxSalary - payBandData?.minSalary;
    const position = payBandData?.currentSalary - payBandData?.minSalary;
    return Math.min(Math.max((position / range) * 100, 0), 100);
  };

  const positionPercentage = calculatePosition();

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-foreground">
          {t('employeePortal.payBand.cardTitle')}
        </h2>
        <div className="flex items-center space-x-2 px-3 py-1 bg-primary/10 rounded-full">
          <Icon name="Award" size={16} className="text-primary" />
          <span className="text-sm font-medium text-primary">{payBandData?.bandLevel}</span>
        </div>
      </div>

      {/* Band Information */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-foreground">{payBandData?.jobTitle}</span>
          <span className="text-sm text-muted-foreground">
            {t('employeePortal.payBand.band', { level: payBandData?.bandLevel })}
          </span>
        </div>
        <p className="text-sm text-muted-foreground">{payBandData?.department} · {payBandData?.location}</p>
      </div>

      {/* Salary Range Visualization */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm text-muted-foreground">
            {t('employeePortal.payBand.minimum')}
          </span>
          <span className="text-sm text-muted-foreground">
            {t('employeePortal.payBand.maximum')}
          </span>
        </div>

        <div className="relative">
          <div className="w-full h-3 bg-muted rounded-full overflow-hidden">
            <div className="absolute inset-0 flex">
              <div className="flex-1 border-r border-background"></div>
              <div className="flex-1 border-r border-background"></div>
              <div className="flex-1 border-r border-background"></div>
              <div className="flex-1"></div>
            </div>
            <div
              className="h-full bg-gradient-to-r from-primary/60 to-primary transition-all duration-500"
              style={{ width: `${positionPercentage}%` }}
            ></div>
          </div>
          <div
            className="absolute top-0 transform -translate-x-1/2 -translate-y-1"
            style={{ left: `${positionPercentage}%` }}
          >
            <div className="w-5 h-5 bg-primary rounded-full border-2 border-background shadow-sm"></div>
          </div>
        </div>

        <div className="flex justify-between items-center mt-2">
          <span className="text-sm font-medium text-foreground">{eur(payBandData?.minSalary)}</span>
          <span className="text-sm font-medium text-foreground">{eur(payBandData?.maxSalary)}</span>
        </div>
      </div>

      {/* Current Position Details */}
      <div className="bg-muted/30 rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-muted-foreground">
            {t('employeePortal.payBand.yourPosition')}
          </span>
          <span className="text-sm font-medium text-primary">
            {t('employeePortal.payBand.percentile', { n: Math.round(positionPercentage) })}
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="MapPin" size={16} className="text-primary" />
          <span className="font-medium text-foreground">{eur(payBandData?.currentSalary)}</span>
          <span className="text-sm text-muted-foreground">
            {t('employeePortal.payBand.withinRange')}
          </span>
        </div>
      </div>

      {/* Band Progression Info */}
      <div className="mt-4 pt-4 border-t border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Icon name="Target" size={16} className="text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              {t('employeePortal.payBand.nextReview')}
            </span>
          </div>
          <span className="text-sm font-medium text-foreground">{payBandData?.nextReview}</span>
        </div>
      </div>
    </div>
  );
};

export default PayBandVisualization;
